import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LAST_ACTIVITY_KEY } from '../../services/_core';

const DEFAULT_IDLE_MS = 15 * 60 * 1000; // 15 minutes
const DEFAULT_WARN_MS = 30 * 1000; // show warning 30s before idle timeout
const CHANNEL_NAME = 'xspectre-auth-events';
const STORAGE_EVENT_KEY = 'xspectre_auth_event';

function now() {
  return Date.now();
}

function createTabId() {
  return `tab_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
}

function readStoredActivity() {
  try {
    const parsed = Number(window.sessionStorage.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredActivity(ts) {
  try {
    window.sessionStorage.setItem(LAST_ACTIVITY_KEY, String(ts));
  } catch {
    /* ignore quota / private-mode */
  }
}

export default function useIdleTimeout({
  enabled,
  onWarn,
  onIdle,
  idleMs = DEFAULT_IDLE_MS,
  warnMs = DEFAULT_WARN_MS,
  sessionMaxMs = 0,
  getSessionStart = null,
} = {}) {
  const [warningSecondsLeft, setWarningSecondsLeft] = useState(null);

  const lastActivityRef = useRef(now());
  const warnedRef = useRef(false);
  const idleTriggeredRef = useRef(false);
  const graceUntilRef = useRef(0);
  const onWarnRef = useRef(onWarn);
  const onIdleRef = useRef(onIdle);
  const getSessionStartRef = useRef(getSessionStart);
  const channelRef = useRef(null);
  const tabIdRef = useRef(createTabId());

  useEffect(() => {
    onWarnRef.current = onWarn;
  }, [onWarn]);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    getSessionStartRef.current = getSessionStart;
  }, [getSessionStart]);

  const resetActivity = useCallback(() => {
    const ts = now();
    lastActivityRef.current = ts;
    writeStoredActivity(ts);
    warnedRef.current = false;
    graceUntilRef.current = 0;
    setWarningSecondsLeft(null);
  }, []);

  const markIdle = useCallback(
    (reason = 'idle_timeout') => {
      if (idleTriggeredRef.current) return;
      idleTriggeredRef.current = true;
      setWarningSecondsLeft(0);
      if (typeof onIdleRef.current === 'function') {
        onIdleRef.current(reason);
      }
    },
    []
  );

  const extendSession = useCallback(async () => {
    const sessionStart = typeof getSessionStartRef.current === 'function'
      ? getSessionStartRef.current()
      : null;
    if (sessionMaxMs > 0 && sessionStart && now() >= sessionStart + sessionMaxMs) {
      markIdle('session_max_exceeded');
      return false;
    }
    idleTriggeredRef.current = false;
    resetActivity();
    return true;
  }, [markIdle, resetActivity, sessionMaxMs]);

  const broadcastLogout = useCallback(() => {
    const payload = JSON.stringify({ type: 'logout', ts: now(), source: tabIdRef.current });
    try {
      if (channelRef.current) {
        channelRef.current.postMessage(payload);
      }
    } catch {
      // ignore cross-tab broadcast errors
    }

    try {
      localStorage.setItem(STORAGE_EVENT_KEY, payload);
      localStorage.removeItem(STORAGE_EVENT_KEY);
    } catch {
      // ignore storage event issues
    }
  }, []);

  const activityEvents = useMemo(
    () => ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'],
    []
  );

  useEffect(() => {
    if (!enabled) {
      setWarningSecondsLeft(null);
      warnedRef.current = false;
      idleTriggeredRef.current = false;
      graceUntilRef.current = 0;
      return undefined;
    }

    idleTriggeredRef.current = false;
    const stored = readStoredActivity();
    const sessionStart = typeof getSessionStartRef.current === 'function'
      ? getSessionStartRef.current()
      : null;
    if (stored && (!sessionStart || stored >= sessionStart)) {
      lastActivityRef.current = stored;
    } else {
      resetActivity();
    }

    const sessionRemainingMs = () => {
      if (!(sessionMaxMs > 0) || typeof getSessionStartRef.current !== 'function') return Infinity;
      const started = getSessionStartRef.current();
      if (!started) return Infinity;
      return started + sessionMaxMs - now();
    };

    const remainingMs = () => {
      const idleRemaining = idleMs - (now() - lastActivityRef.current);
      return Math.min(idleRemaining, sessionRemainingMs());
    };

    const expireReason = () => (
      sessionRemainingMs() <= 0 ? 'session_max_exceeded' : 'idle_timeout'
    );

    const startWarning = (seconds) => {
      const safeSeconds = Math.max(1, Math.ceil(seconds));
      setWarningSecondsLeft(safeSeconds);
      if (!warnedRef.current) {
        warnedRef.current = true;
        if (typeof onWarnRef.current === 'function') onWarnRef.current();
      }
    };

    const evaluate = () => {
      if (idleTriggeredRef.current) return;

      const remaining = remainingMs();

      if (remaining <= 0) {
        if (!warnedRef.current && warnMs > 0) {
          graceUntilRef.current = now() + warnMs;
          startWarning(warnMs / 1000);
          return;
        }
        if (graceUntilRef.current > now()) {
          startWarning((graceUntilRef.current - now()) / 1000);
          return;
        }
        markIdle(expireReason());
        return;
      }

      if (remaining <= warnMs) {
        graceUntilRef.current = 0;
        startWarning(remaining / 1000);
        return;
      }

      if (warnedRef.current) {
        warnedRef.current = false;
        graceUntilRef.current = 0;
      }
      setWarningSecondsLeft((prev) => (prev === null ? prev : null));
    };

    const onActivity = () => {
      if (idleTriggeredRef.current) return;
      // While the countdown dialog is open, ignore mouse/keyboard noise.
      // Only "Stay Signed In" (extendSession) should reset the timer.
      if (warnedRef.current) return;
      resetActivity();
    };

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });

    const onVisibility = () => {
      if (document.visibilityState === 'visible') evaluate();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', evaluate);

    evaluate();
    const interval = window.setInterval(evaluate, 250);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', evaluate);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
    };
  }, [activityEvents, enabled, idleMs, markIdle, resetActivity, sessionMaxMs, warnMs]);

  useEffect(() => {
    if (!enabled) return undefined;

    const handleCrossTabLogout = () => {
      markIdle('auth_expired');
    };

    if (typeof BroadcastChannel !== 'undefined') {
      channelRef.current = new BroadcastChannel(CHANNEL_NAME);
      const listener = (event) => {
        if (!event?.data) return;
        try {
          const payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (payload?.type === 'logout' && payload?.source !== tabIdRef.current) handleCrossTabLogout();
        } catch {
          // ignore malformed payload
        }
      };

      channelRef.current.addEventListener('message', listener);

      return () => {
        try {
          channelRef.current?.removeEventListener('message', listener);
          channelRef.current?.close();
        } catch {
          // ignore close errors
        }
        channelRef.current = null;
      };
    }

    const onStorage = (event) => {
      if (event.key !== STORAGE_EVENT_KEY || !event.newValue) return;
      try {
        const payload = JSON.parse(event.newValue);
        if (payload?.type === 'logout' && payload?.source !== tabIdRef.current) handleCrossTabLogout();
      } catch {
        // ignore malformed payload
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [enabled, markIdle]);

  return {
    extendSession,
    warningSecondsLeft,
    broadcastLogout,
  };
}
